import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { Spinner } from '../../design-system';

interface StepConfig {
  title: string;
  subtitle: string;
  questions: QuestionConfig[];
}

interface QuestionConfig {
  key: string;
  label: string;
  options: string[];
  type: 'select' | 'text';
  required: boolean;
}

const STEPS: StepConfig[] = [
  {
    title: 'About You',
    subtitle: 'Help us understand your background',
    questions: [
      {
        key: 'role',
        label: 'What best describes your role?',
        type: 'select',
        required: true,
        options: [
          'Developer / Engineer',
          'Security / AppSec Engineer',
          'Team Lead / Engineering Manager',
          'CISO / VP Security / Director',
          'Other',
        ],
      },
      {
        key: 'team_size',
        label: 'How big is your engineering team?',
        type: 'select',
        required: true,
        options: ['Just me', '2\u201310', '11\u201350', '51\u2013200', '200+'],
      },
    ],
  },
  {
    title: 'Your Goals',
    subtitle: 'What are you looking to achieve?',
    questions: [
      {
        key: 'what_brought_you',
        label: 'What brought you here today?',
        type: 'select',
        required: true,
        options: [
          'Just exploring / curiosity',
          'Evaluating for a specific project',
          'Comparing tools \u2014 we\u2019re looking to buy',
          'Someone on my team recommended it',
        ],
      },
      {
        key: 'adoption_process',
        label: 'How do security tools typically get adopted at your company?',
        type: 'select',
        required: true,
        options: [
          'Developers choose their own tools',
          'Team leads evaluate and decide',
          'There\u2019s a formal procurement / approval process',
          'Not sure / I\u2019m working on a personal project',
        ],
      },
    ],
  },
  {
    title: 'One Last Thing',
    subtitle: 'Almost done!',
    questions: [
      {
        key: 'how_heard',
        label: 'How did you hear about Kolega?',
        type: 'select',
        required: true,
        options: [
          'Google / search',
          'LinkedIn',
          'Reddit / Hacker News',
          'A colleague or friend',
          'Blog post or article',
          'Conference or event',
          'Other',
        ],
      },
      {
        key: 'biggest_challenge',
        label: 'What\u2019s your biggest security challenge right now? (optional)',
        type: 'text',
        required: false,
        options: [],
      },
    ],
  },
];

const LeadQuestionnaireModal: React.FC = () => {
  const [shouldShow, setShouldShow] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const api = useAuthenticatedApi();

  useEffect(() => {
    api.get<{ should_show: boolean }>('/lead-questionnaire/status')
      .then((res) => setShouldShow(res.should_show))
      .catch(() => {});
  }, []);

  const handleDismiss = useCallback(async () => {
    setShouldShow(false);
    try {
      await api.post('/lead-questionnaire/dismiss');
    } catch {
      // Silently fail — don't block the user
    }
  }, [api]);

  useEffect(() => {
    if (!shouldShow) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleDismiss();
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [shouldShow, handleDismiss]);

  const setAnswer = (key: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const isStepComplete = () => {
    const step = STEPS[currentStep];
    return step.questions
      .filter((q) => q.required)
      .every((q) => answers[q.key]);
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await api.post('/lead-questionnaire/submit', answers);
      setShouldShow(false);
    } catch {
      // Allow retry
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!shouldShow) return null;

  const step = STEPS[currentStep];
  const isLastStep = currentStep === STEPS.length - 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={handleDismiss}
      />

      {/* Modal */}
      <div
        className="relative bg-ui-background-secondary rounded-2xl shadow-2xl max-w-lg w-full border border-ui-border-primary animate-in zoom-in-95 slide-in-from-bottom-2 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <div>
            <h2 className="text-lg font-semibold text-ui-text-primary">
              {step.title}
            </h2>
            <p className="text-sm text-ui-text-tertiary mt-0.5">
              {step.subtitle}
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="text-ui-text-tertiary hover:text-ui-text-primary transition-colors p-1 rounded-lg hover:bg-ui-background-tertiary"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex gap-1.5 px-6 pt-2 pb-4">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors ${
                i <= currentStep
                  ? 'bg-brand-primary dark:bg-brand-primaryDarkMode'
                  : 'bg-ui-background-tertiary'
              }`}
            />
          ))}
        </div>

        {/* Questions */}
        <div className="px-6 pb-4 space-y-5 max-h-[60vh] overflow-y-auto">
          {step.questions.map((question) => (
            <div key={question.key}>
              <label className="block text-sm font-medium text-ui-text-secondary mb-2">
                {question.label}
              </label>

              {question.type === 'select' ? (
                <div className="space-y-1.5">
                  {question.options.map((option) => {
                    const isSelected = answers[question.key] === option;
                    return (
                      <button
                        key={option}
                        onClick={() => setAnswer(question.key, option)}
                        className={`w-full text-left px-3.5 py-2.5 rounded-lg text-sm transition-all border ${
                          isSelected
                            ? 'border-brand-primary bg-brand-primary/10 text-ui-text-primary dark:border-brand-primaryDarkMode dark:bg-brand-primaryDarkMode/10 dark:text-brand-primaryDarkMode'
                            : 'border-ui-border-primary text-ui-text-secondary hover:border-ui-border-primary dark:hover:border-ui-border-primary hover:bg-ui-background-tertiary'
                        }`}
                      >
                        <span className="flex items-center gap-2.5">
                          <span
                            className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                              isSelected
                                ? 'border-brand-primary dark:border-brand-primaryDarkMode'
                                : 'border-ui-border-primary'
                            }`}
                          >
                            {isSelected && (
                              <span className="w-2 h-2 rounded-full bg-brand-primary dark:bg-brand-primaryDarkMode" />
                            )}
                          </span>
                          {option}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <textarea
                  value={answers[question.key] || ''}
                  onChange={(e) => setAnswer(question.key, e.target.value)}
                  placeholder="Tell us what's on your mind..."
                  rows={3}
                  className="w-full px-3.5 py-2.5 bg-ui-background-secondary border border-ui-border-primary rounded-lg text-sm text-ui-text-primary placeholder-ui-text-tertiary dark:placeholder-ui-text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all resize-none"
                />
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-ui-border-primary">
          <button
            onClick={currentStep === 0 ? handleDismiss : handleBack}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-ui-text-secondary hover:bg-ui-background-tertiary transition-colors"
          >
            {currentStep === 0 ? (
              'Skip'
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" />
                Back
              </>
            )}
          </button>

          {isLastStep ? (
            <button
              onClick={handleSubmit}
              disabled={!isStepComplete() || isSubmitting}
              className={`flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-medium text-black transition-all ${
                isStepComplete() && !isSubmitting
                  ? 'bg-brand-primary hover:bg-brand-primaryDark dark:bg-brand-primaryDarkMode dark:hover:bg-brand-primary'
                  : 'bg-ui-background-tertiary cursor-not-allowed opacity-50'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Spinner size={16} tone="current" />
                  Submitting...
                </span>
              ) : (
                <>
                  Submit
                  <Check className="w-4 h-4" />
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={!isStepComplete()}
              className={`flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-medium text-black transition-all ${
                isStepComplete()
                  ? 'bg-brand-primary hover:bg-brand-primaryDark dark:bg-brand-primaryDarkMode dark:hover:bg-brand-primary'
                  : 'bg-ui-background-tertiary cursor-not-allowed opacity-50'
              }`}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadQuestionnaireModal;
